import { readonly, watch } from "vue";
import { useState, useColorMode, useAppConfig } from "#imports";

export const useThemeSettings = () => {
  const appConfig = useAppConfig();
  const colorMode = useColorMode();

  // Available colors configuration
  const availableColors = [
    { name: "blue", value: "#3B82F6" },
    { name: "green", value: "#10B981" },
    { name: "purple", value: "#8B5CF6" },
    { name: "red", value: "#EF4444" },
    { name: "yellow", value: "#F59E0B" },
    { name: "pink", value: "#EC4899" },
    { name: "indigo", value: "#6366F1" },
    { name: "teal", value: "#14B8A6" },
    { name: "orange", value: "#F97316" },
    { name: "cyan", value: "#06B6D4" },
    { name: "lime", value: "#84CC16" },
    { name: "emerald", value: "#059669" },
  ];

  // Global state using useState
  const primaryColor = useState("primaryColor", () => {
    return appConfig.ui.colors.primary || "blue";
  });

  // Initialize from localStorage
  const initializeThemeSettings = () => {
    if (import.meta.client) {
      // Initialize primary color
      const savedColor = localStorage.getItem("nuxt-primary-color");
      if (savedColor && availableColors.find((c) => c.name === savedColor)) {
        primaryColor.value = savedColor;
        appConfig.ui.colors.primary = savedColor;
        // updateCSSCustomProperty(savedColor);
      }

      // Initialize color mode
      const savedColorMode = localStorage.getItem("nuxt-color-mode");
      if (
        savedColorMode &&
        ["light", "dark", "system"].includes(savedColorMode)
      ) {
        colorMode.preference = savedColorMode;
      }
    }
  };

  // Update CSS custom property
  // const updateCSSCustomProperty = (colorName) => {
  //   if (import.meta.client) {
  //     // const colorValue =
  //     //   availableColors.find((c) => c.name === colorName)?.value || "#3B82F6";
  //     appConfig.ui.colors.primary = colorName;
  //     // document.documentElement.style.setProperty("--primary-color", colorValue);
  //   }
  // };

  // Set primary color with persistence
  const setPrimaryColor = (color) => {
    if (!availableColors.find((c) => c.name === color)) {
      console.warn(`Color "${color}" is not available`);
      return;
    }

    primaryColor.value = color;
    appConfig.ui.colors.primary = color;
    // updateCSSCustomProperty(color);

    if (import.meta.client) {
      localStorage.setItem("nuxt-primary-color", color);
    }
  };

  // Set color mode with persistence
  const setColorMode = (mode) => {
    if (!["light", "dark", "system"].includes(mode)) {
      console.warn(`Color mode "${mode}" is not valid`);
      return;
    }

    colorMode.preference = mode;

    if (import.meta.client) {
      localStorage.setItem("nuxt-color-mode", mode);
    }
  };

  // Get color value by name
  const getColorValue = (colorName) => {
    return (
      availableColors.find((c) => c.name === colorName)?.value || "#3B82F6"
    );
  };

  // Reset all theme settings
  const resetThemeSettings = () => {
    setPrimaryColor("blue");
    setColorMode("system");

    if (import.meta.client) {
      localStorage.removeItem("nuxt-primary-color");
      localStorage.removeItem("nuxt-color-mode");
    }
  };

  // Watch for changes and persist them
  watch(primaryColor, (newColor) => {
    // updateCSSCustomProperty(newColor);
    if (import.meta.client) {
      localStorage.setItem("nuxt-primary-color", newColor);
    }
  });

  watch(
    () => colorMode.preference,
    (newMode) => {
      if (import.meta.client) {
        localStorage.setItem("nuxt-color-mode", newMode);
      }
    }
  );

  return {
    primaryColor: readonly(primaryColor),
    colorMode,
    availableColors,
    setPrimaryColor,
    setColorMode,
    getColorValue,
    initializeThemeSettings,
    resetThemeSettings,
  };
};
